import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { FreeMode } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useConfiguration } from '../../../../context/configurationContext';
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';

import { createResourceLocatorString } from '../../../../util/routes';
import { constructQueryParamName } from '../../../../util/search';

import SectionContainer from '../SectionContainer';

import hiking from '../../../../assets/HIKING_AdobeStock_216687583.webp';
import camping from '../../../../assets/CAMPING_AdobeStock_533763874.webp';
import climbing from '../../../../assets/CLIMBING_AdobeStock_277080158.webp';
import cycling from '../../../../assets/CYCLING_AdobeStock_267990382.webp';
import snowSports from '../../../../assets/SNOW SPORTS_AdobeStock_132110847.webp';
import waterSports from '../../../../assets/WATER SPORTS_AdobeStock_211173914.webp';

import iconHiking from '../../../../assets/icon-hiking.svg';
import iconCamping from '../../../../assets/icon-camping.svg';
import iconClimbing from '../../../../assets/icon-climbing.svg';
import iconCycling from '../../../../assets/icon-cycling.svg';
import iconSnowSports from '../../../../assets/icon-snow-sports.svg';
import iconWaterSports from '../../../../assets/icon-water-sports.svg';

import css from './ImagesGrid.module.css';

// Swiper CSS is browser-only; top-level import breaks Node SSR (Heroku: external commonjs "swiper/css").

/** Decorative image fallback when category cannot be identified from id/name. */
const CATEGORY_GRID_IMAGES = [hiking, camping, climbing, cycling, snowSports, waterSports];

const CATEGORY_IMAGE_BY_KEYWORD = [
  { keywords: ['hiking'], image: hiking },
  { keywords: ['camping'], image: camping },
  { keywords: ['climbing', 'climb'], image: climbing },
  { keywords: ['cycling', 'cycle', 'bike'], image: cycling },
  { keywords: ['snow'], image: snowSports },
  { keywords: ['water'], image: waterSports },
];

const CATEGORY_ICON_BY_KEYWORD = [
  { keywords: ['hiking'], icon: iconHiking },
  { keywords: ['camping'], icon: iconCamping },
  { keywords: ['climbing', 'climb'], icon: iconClimbing },
  { keywords: ['cycling', 'cycle', 'bike'], icon: iconCycling },
  { keywords: ['snow'], icon: iconSnowSports },
  { keywords: ['water'], icon: iconWaterSports },
];

const resolveCategoryAssets = (category, index) => {
  const id = typeof category?.id === 'string' ? category.id.toLowerCase() : '';
  const name = typeof category?.name === 'string' ? category.name.toLowerCase() : '';
  const searchableValue = `${id} ${name}`;

  const matchedImage = CATEGORY_IMAGE_BY_KEYWORD.find(({ keywords }) =>
    keywords.some(keyword => searchableValue.includes(keyword))
  );
  const matchedIcon = CATEGORY_ICON_BY_KEYWORD.find(({ keywords }) =>
    keywords.some(keyword => searchableValue.includes(keyword))
  );

  return {
    image: matchedImage?.image || CATEGORY_GRID_IMAGES[index % CATEGORY_GRID_IMAGES.length],
    icon: matchedIcon?.icon || null,
  };
};

const ImagesGrid = props => {
  const { sectionId, className, rootClassName, appearance, options } = props;
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();

  useEffect(() => {
    import('swiper/css');
  }, []);

  const categorySlides = useMemo(() => {
    const listingCategories = config?.categoryConfiguration?.categories;
    const categoryKeyPrefix = config?.categoryConfiguration?.key;
    if (!listingCategories?.length || !categoryKeyPrefix) {
      return [];
    }

    const categoryQueryParam = constructQueryParamName(`${categoryKeyPrefix}1`, 'public');

    return listingCategories.map((category, index) => {
      const { image, icon } = resolveCategoryAssets(category, index);
      return {
        id: category.id,
        title: category.name,
        image,
        icon,
        to: createResourceLocatorString('SearchPage', routeConfiguration, {}, {
          [categoryQueryParam]: category.id,
        }),
      };
    });
  }, [config?.categoryConfiguration, routeConfiguration]);

  if (categorySlides.length === 0) {
    return null;
  }

  return (
    <SectionContainer
      id={sectionId}
      rootClassName={classNames(rootClassName || css.root)}
      className={className}
      appearance={appearance}
      options={options}
    >
      <div className={css.inner}>
        <h2 className={css.mainTitle}>Explore by Activity</h2>
        <Swiper
          className={css.swiper}
          modules={[FreeMode]}
          slidesPerView={2}
          spaceBetween={26}
          freeMode={{ enabled: true, momentumRatio: 0.85 }}
          loop
          grabCursor
          breakpoints={{
            768: {
              slidesPerView: 3,
              spaceBetween: 26,
              freeMode: false,
            },
            1024: {
              slidesPerView: 4,
              spaceBetween: 26,
              freeMode: false,
            },
          }}
        >
          {categorySlides.map(item => (
            <SwiperSlide key={item.id} className={css.slide}>
              <Link to={item.to} className={css.slideLink}>
                <div className={css.item}>
                  <div className={css.imageWrapper}>
                    <img src={item.image} alt={item.title} className={css.photo} />
                    <div className={css.imageOverlay} aria-hidden="true" />
                    <div className={css.imageCaption}>
                      {item.icon && (
                        <img
                          src={item.icon}
                          alt=""
                          aria-hidden="true"
                          className={css.categoryIcon}
                        />
                      )}
                      <h3 className={css.imageTitle}>{item.title}</h3>
                    </div>
                  </div>
                </div>
              </Link>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </SectionContainer>
  );
};

export default ImagesGrid;
